import { AccountBox } from "@mui/icons-material";
import { Box, Button, Menu, MenuItem } from "@mui/material";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { useLogout } from "api/services/useAuth";
import { setIsLoggingOut } from "api/utils";
import { getConfig } from "config/config";
import { TierBadge } from "features/appBar/components/TierBadge";
import { useResetOnboardings } from "hooks/onboardings/useResetOnboardings";
import { useAppDispatch, useAppSelector } from "hooks/store";
import { useIsUserSciduck } from "hooks/useIsUserSciduck/useIsUserSciduck";
import { selectBlinkUsername, setBlinkUsername } from "store/slices/Account/accountSlice";
import { User } from "types/users";
import { getPostLogoutRedirectUrl, replaceWindowLocation } from "utils/utilityFunctions";

const config = getConfig();
const { REACT_APP_LOGOUT_URL } = config;

interface AccountMenuProps {
  user?: User;
}

/** Renders `{group} / {email}` with the email as the primary label. */
function AccountBarIdentity({ user }: { user: User }) {
  const groupName = user.groups?.[0]?.name;

  return (
    <Box component="span" className="flex items-baseline">
      {groupName ? (
        <>
          <Box component="span" sx={{ color: "rgba(255, 255, 255, 0.55)", textTransform: "none" }}>
            {groupName}
          </Box>
          <Box
            component="span"
            sx={{ color: "rgba(255, 255, 255, 0.35)", mx: "0.35em", textTransform: "none" }}
          >
            /
          </Box>
        </>
      ) : null}
      <Box component="span" className="text-white lowercase">
        {user.email}
      </Box>
    </Box>
  );
}

export default function AccountMenu(props: AccountMenuProps) {
  const { user } = props;
  const isUserSciDuck = useIsUserSciduck();
  const dispatch = useAppDispatch();

  const [menuAnchorElement, setMenuAnchorElement] = useState<null | HTMLElement>(null);
  const resetOnboardings = useResetOnboardings();

  const { refetch: logout } = useLogout();

  const [isMdOrLarger, setIsMdOrLarger] = useState(false);

  useEffect(() => {
    const updateSize = () => {
      setIsMdOrLarger(window.innerWidth >= 768);
    };

    updateSize(); // Initial check
    window.addEventListener("resize", updateSize);
    return () => window.removeEventListener("resize", updateSize);
  }, []);

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    dispatch(setBlinkUsername(false));

    if (setMenuAnchorElement) {
      setMenuAnchorElement(event.currentTarget);
    }
  };
  const handleCloseMenu = () => {
    setMenuAnchorElement(null);
  };

  async function handleSignOut() {
    handleCloseMenu();
    const logoutResponse = await logout();
    setIsLoggingOut();
    replaceWindowLocation(getPostLogoutRedirectUrl(logoutResponse, REACT_APP_LOGOUT_URL));
  }

  const shouldShowBlinkingAnimation = useAppSelector(selectBlinkUsername);

  return (
    <div>
      {user ? (
        <Box>
          <Button
            onClick={handleMenu}
            className="text-white text-xl leading-6 p-0 py-[6px] font-normal"
            sx={{
              ...(shouldShowBlinkingAnimation && {
                animation: "blinking 1.2s infinite",
                "@keyframes blinking": {
                  "0%": { opacity: 1 },
                  "50%": { opacity: 0.5 },
                  "100%": { opacity: 1 },
                },
              }),
            }}
          >
            {isMdOrLarger ? <AccountBarIdentity user={user} /> : null}
            <TierBadge
              customerType={user?.userprofile?.customer_type}
              tier={user.tier}
              isSuperuser={user.is_superuser}
              sx={{ marginTop: "4px" }}
            />
            <Box className="ml-2">
              <AccountBox className="h-[32px] w-[32px]" />
            </Box>
          </Button>
          <Menu
            id="menu-appbar"
            anchorEl={menuAnchorElement}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            open={Boolean(menuAnchorElement)}
            onClose={handleCloseMenu}
          >
            {!user.only_labeler_ui ? ( // Labelers do not have Account access
              <Link to="/reef/my-account">
                <MenuItem onClick={handleCloseMenu}>Account</MenuItem>
              </Link>
            ) : undefined}

            {!user.only_labeler_ui && !isUserSciDuck ? ( // Labelers do not have onboarding modal access
              <MenuItem
                onClick={async () => {
                  await resetOnboardings();
                  handleCloseMenu();
                }}
              >
                Quick start
              </MenuItem>
            ) : undefined}

            {!user.only_labeler_ui ? ( // Labelers do not have API token access
              <Link to="/reef/my-account/api-tokens">
                <MenuItem onClick={handleCloseMenu}>Api tokens</MenuItem>
              </Link>
            ) : undefined}
            <MenuItem onClick={() => handleSignOut()}>Logout</MenuItem>
          </Menu>
        </Box>
      ) : (
        <Link to="/auth/login">
          <Button className="text-white text-xl px-4 py-2 border-b min-h-[48px]">Login</Button>
        </Link>
      )}
    </div>
  );
}
